import type {
  LicenseApiError,
  LicenseResponseBody,
} from '@/features/Licenses/api/licenseHooks'
import {
  invalidateOrgLicenses,
  setOrgLicenseQueryData,
} from '@/features/Licenses/api/licenseHooks'
import {
  ERROR_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import type { QueryClient } from '@tanstack/react-query'

export type InDialogError = {
  detail: string
  correlationId?: string
}

/**
 * Maps a license-action mutation's error state to the in-dialog Alert payload,
 * or null if no inline alert should render. Statuses NOT in `inlineStatuses`
 * fall through to the toast path (handled by `buildMutationOptions`).
 *
 * `correlationIdStatuses` is the subset of `inlineStatuses` for which the
 * correlationId line should be rendered (typically [502] — gateway hops are
 * where correlation matters; 400 is a client-side validation surface).
 */
export function mapMutationError(
  mutation: {
    isError: boolean
    error: LicenseApiError | null
  },
  args: {
    inlineStatuses: number[]
    correlationIdStatuses?: number[]
    fallbackDetail: string
  },
): InDialogError | null {
  const error = mutation.isError ? mutation.error : null
  if (!error) {
    return null
  }
  const status = error.status ?? 0
  if (!args.inlineStatuses.includes(status)) {
    return null
  }
  return {
    detail: error.payload?.detail ?? error.message ?? args.fallbackDetail,
    correlationId: args.correlationIdStatuses?.includes(status)
      ? error.payload?.correlationId
      : undefined,
  }
}

/**
 * Builds the `onSuccess` + `onError` callbacks shared by every license
 * lifecycle action mutation:
 *   - onSuccess invalidates the org's license queries and runs the caller's
 *     success handler (typically closing the dialog).
 *   - onError suppresses the toast for `inlineStatuses` (rendered as the
 *     in-dialog Alert via `mapMutationError`) and shows a retryable toast
 *     for everything else.
 */
export function buildMutationOptions(args: {
  queryClient: QueryClient
  license: Pick<LicenseResponseBody, 'orgId' | 'id'>
  onSuccess: () => void
  toast: {
    id: string
    title: string
    description: string
    retry?: { label: string; onClick: () => void }
  }
  inlineStatuses: number[]
}) {
  const { queryClient, license, onSuccess, toast, inlineStatuses } = args
  return {
    onSuccess: (data: LicenseResponseBody) => {
      setOrgLicenseQueryData(queryClient, license.id, data)
      invalidateOrgLicenses(queryClient, license.orgId)
      onSuccess()
    },
    onError: (error: LicenseApiError) => {
      const status = error.status ?? 0
      if (inlineStatuses.includes(status)) {
        return
      }
      showOrUpdateToast(toast.id, {
        ...ERROR_TOAST_OPTION,
        title: toast.title,
        description: toast.description,
        ...(toast.retry
          ? {
              action: {
                label: toast.retry.label,
                onClick: toast.retry.onClick,
              },
            }
          : {}),
      })
    },
  }
}
